/**
 * Thin, typed wrapper around `@elevenlabs/client`'s Conversation.
 *
 * Why a wrapper at all? Three reasons:
 *   1. Component code shouldn't know SDK shapes — easier to swap or stub later.
 *   2. The SDK exposes status + mode separately; the component wants a single
 *      flat state machine (idle → connecting → listening / speaking → idle).
 *   3. The SDK reports audio levels via pull APIs (`getInputVolume()`); the
 *      orb wants push events. We poll on rAF and dispatch.
 *
 * Events (CustomEvent detail shapes):
 *   - "state"    { state: ConvState }                       any state change
 *   - "message"  { source: "user" | "ai", text: string }    transcript chunk
 *   - "level"    { input: number, output: number }          0..1, ~60Hz while connected
 *   - "error"    { message: string }                        recoverable / fatal
 *   - "end"      { reason: "user" | "agent" | "error" }     after disconnect
 *   - "cta"      { kind: "schedule_demo", url: string }     UI should reveal a CTA
 *
 * Client tools registered with the SDK:
 *   - schedule_demo()                                       opens /book in a new
 *                                                           tab + dispatches "cta"
 */

const SCHEDULE_DEMO_URL = 'https://neutrix.co/book';

import { Conversation } from '@elevenlabs/client';
import type { Mode, Status } from '@elevenlabs/client';

export type ConvState = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error';

export interface StartOptions {
  agentId: string;
  dynamicVariables?: Record<string, string | number | boolean>;
}

export type ConvEventDetail = {
  state: { state: ConvState };
  message: { source: 'user' | 'ai'; text: string };
  level: { input: number; output: number };
  error: { message: string };
  end: { reason: 'user' | 'agent' | 'error' };
  cta: { kind: 'schedule_demo'; url: string };
};

type EventName = keyof ConvEventDetail;

export class NeutrixConversation extends EventTarget {
  private conv: Conversation | null = null;
  private rafHandle: number | null = null;
  private state: ConvState = 'idle';
  private mode: Mode = 'listening';

  /** Current high-level state (last value emitted on the "state" event). */
  getState(): ConvState {
    return this.state;
  }

  /** Begin a session. Resolves once the SDK reports `connected`, or rejects on failure. */
  async start(opts: StartOptions): Promise<void> {
    if (this.conv) return;
    this.setState('connecting');

    try {
      this.conv = await Conversation.startSession({
        agentId: opts.agentId,
        ...(opts.dynamicVariables ? { dynamicVariables: opts.dynamicVariables } : {}),
        // Client tools run in the browser, not via webhook. We register one:
        // `schedule_demo` opens the booking page and surfaces a visible CTA so
        // the visitor doesn't depend on popup-blockers cooperating.
        clientTools: {
          schedule_demo: () => {
            try {
              window.open(SCHEDULE_DEMO_URL, '_blank', 'noopener,noreferrer');
            } catch {
              // Popup blockers swallow this — the CTA card we dispatch below
              // is the safety net; visitor can still click it.
            }
            this.dispatchTyped('cta', { kind: 'schedule_demo', url: SCHEDULE_DEMO_URL });
            return 'Booking page opened.';
          },
        },
        onConnect: () => this.startLevelPump(),
        onStatusChange: ({ status }) => this.handleStatus(status),
        onModeChange: ({ mode }) => this.handleMode(mode),
        onMessage: ({ source, message }) => {
          this.dispatchTyped('message', { source, text: message });
          // The SDK fires onMessage(source='user') when the user's utterance
          // is finalized. After that, the LLM thinks for ~0.5–2s before TTS
          // starts. During that window the SDK's mode is still 'listening',
          // which looks identical to "user is speaking" in the widget. Fill
          // the gap with an explicit 'thinking' state so the visitor sees
          // *something* changed and doesn't think it's broken.
          if (source === 'user' && this.state === 'listening') {
            this.setState('thinking');
          }
        },
        onError: (message, context) => {
          this.setState('error');
          // The SDK sometimes hands back the literal "[object CloseEvent]"
          // string when a WebSocket terminates; the real info lives in
          // `context`. Pull whatever we can to make the error actionable.
          let detail = String(message);
          if (context && typeof context === 'object') {
            const ce = context as { code?: number; reason?: string; type?: string; message?: string; wasClean?: boolean };
            const parts: string[] = [];
            if (ce.code !== undefined) parts.push(`code=${ce.code}`);
            if (ce.reason) parts.push(`reason="${ce.reason}"`);
            if (ce.type) parts.push(`type=${ce.type}`);
            if (ce.message && ce.message !== detail) parts.push(`msg="${ce.message}"`);
            if (ce.wasClean !== undefined) parts.push(`wasClean=${ce.wasClean}`);
            if (parts.length > 0) detail = `${detail} (${parts.join(', ')})`;
          }
          // Dump the raw context to the console too — easier for a human to inspect.
          console.error('[neutrix-conversation] onError', { message, context });
          this.dispatchTyped('error', { message: detail });
        },
        onDisconnect: (details) => {
          this.stopLevelPump();
          // If the disconnect carries an error or an unexpected agent close,
          // surface it on the error channel with useful detail rather than
          // silently dropping back to idle.
          if (details.reason === 'error') {
            this.setState('error');
            this.dispatchTyped('error', {
              message: `disconnected: ${details.message}`,
            });
          } else if (details.reason === 'agent') {
            const ce = details.context;
            // wasClean=false + code=1006 typically means the server rejected
            // the upgrade (allowlist, auth, no signed URL, etc.). Surface it.
            const clean = ce.wasClean === true;
            if (!clean) {
              this.setState('error');
              this.dispatchTyped('error', {
                message: `agent disconnect (code=${ce.code}, reason=${ce.reason || '(empty)'}, wasClean=${clean})`,
              });
            } else {
              this.setState('idle');
            }
          } else {
            this.setState('idle');
          }
          this.dispatchTyped('end', { reason: details.reason });
        },
      });
    } catch (err) {
      this.setState('error');
      // The SDK throws a raw CloseEvent (not an Error) when the WebSocket
      // closes before the conversation metadata arrives. Extract code/reason
      // because they tell us *why* the server rejected (e.g. allowlist).
      let message: string;
      if (err && typeof err === 'object' && 'code' in err && 'wasClean' in err) {
        const ce = err as CloseEvent;
        message = `WebSocket closed before metadata: code=${ce.code}, reason="${ce.reason || '(empty)'}", wasClean=${ce.wasClean}`;
      } else if (err instanceof Error) {
        message = err.message;
      } else {
        message = String(err);
      }
      console.error('[neutrix-conversation] startSession failed', { err, message });
      this.dispatchTyped('error', { message });
      throw err;
    }
  }

  /** End the session cleanly. Idempotent. */
  async stop(): Promise<void> {
    this.stopLevelPump();
    if (this.conv) {
      try {
        await this.conv.endSession();
      } finally {
        this.conv = null;
      }
    }
    if (this.state !== 'idle') {
      this.setState('idle');
      this.dispatchTyped('end', { reason: 'user' });
    }
  }

  setMicMuted(muted: boolean): void {
    this.conv?.setMicMuted(muted);
  }

  /**
   * Typed event subscription. Returns an unsubscribe function so callers don't
   * have to track listener references for `removeEventListener`.
   */
  on<T extends EventName>(
    type: T,
    listener: (detail: ConvEventDetail[T]) => void,
  ): () => void {
    const handler = (ev: Event): void => {
      listener((ev as CustomEvent<ConvEventDetail[T]>).detail);
    };
    this.addEventListener(type, handler);
    return () => this.removeEventListener(type, handler);
  }

  // ---- internals ---------------------------------------------------------

  private handleStatus(status: Status): void {
    switch (status) {
      case 'connecting':
        this.setState('connecting');
        return;
      case 'connected':
        // Map mode-aware once connected.
        this.setState(this.mode === 'speaking' ? 'speaking' : 'listening');
        return;
      case 'disconnecting':
      case 'disconnected':
        this.stopLevelPump();
        this.setState('idle');
        return;
    }
  }

  private handleMode(mode: Mode): void {
    this.mode = mode;
    // Only flip the state when we're already in an active sub-state — connecting
    // and error states must stick until status changes. We include 'thinking'
    // here so when the AI starts speaking we transition out of thinking.
    if (this.state === 'listening' || this.state === 'speaking' || this.state === 'thinking') {
      this.setState(mode === 'speaking' ? 'speaking' : 'listening');
    }
  }

  private setState(state: ConvState): void {
    if (this.state === state) return;
    this.state = state;
    this.dispatchTyped('state', { state });
  }

  private dispatchTyped<T extends EventName>(type: T, detail: ConvEventDetail[T]): void {
    this.dispatchEvent(new CustomEvent(type, { detail }));
  }

  private startLevelPump(): void {
    if (this.rafHandle !== null) return;
    const tick = (): void => {
      if (!this.conv) {
        this.rafHandle = null;
        return;
      }
      const input = clamp01(this.conv.getInputVolume());
      const output = clamp01(this.conv.getOutputVolume());
      this.dispatchTyped('level', { input, output });
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  private stopLevelPump(): void {
    if (this.rafHandle !== null) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }
  }
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
